<template>
  <div class="cart-item">
    <AtomsGlobalLink :to="props.url" class="cart-item__image">
      <MoleculesGlobalWareImage :image-id="props.imageId" suffix="pl" />
    </AtomsGlobalLink>

    <div class="cart-item__description">
      <AtomsGlobalLink :to="props.url" class="font-bold text-xl underline tracking-normal">
        {{ props.name }}
      </AtomsGlobalLink>
      <div v-if="props.hasLimitedShipping" class="text-error-600">
        {{ $t('Product with limited shipping options outside EU') }} <button @click="openLimitedShippingDialog">(<span class="underline">{{ $t('find out more') }})</span></button>.
      </div>
      <div class="text-lg">{{ props.variantName }}</div>
      <div v-if="props.code" class="text-sm text-neutral-300">{{ props.code }}</div>
      <Transition>
        <MoleculesButtonsButton v-if="showEngravingButton" @click="useCart().openServiceDialogForCartItem(props.presentationCartItem, 'Košík')" :secondary-ghost="true" class="order-2">+ {{ $t('Přikoupit gravírování') }}</MoleculesButtonsButton>
      </Transition>
    </div>

    <div class="cart-item__availability">{{ props.availabilityLabel ? $t(props.availabilityLabel) : '' }}</div>
    <div class="cart-item__quantity-with-price">
      <MoleculesFormsQuantityField class="cart-item__quantity" v-model="props.modelValue" @update:modelValue="update($event)" :unit="props.unit" :min="1" />
      <div class="cart-item__price">
        <div class="text-xl font-bold">
          <MoleculesGlobalAutocounter :value="props.totalPrice" />
        </div>
        <div :class="{ 'invisible': props.modelValue === 1 }" class="cart-item__price__piece">{{ props.piecePriceFormatted }}</div>
      </div>
    </div>

    <button v-if="!props.hideRemoveButton" class="cart-item__remove" @click="removeCartItem" @mouseover="removeHover = true" @mouseleave="removeHover = false">
      <Transition><span v-show="removeHover">{{ $t('Odebrat položku') }}</span></Transition>
      <AtomsImagesIcon icon="cross" size="xs" class="ml-2" />
    </button>
  </div>

  <!-- ware services -->

  <div v-for="serviceItem in orderedServices" class="cart-item" :key="serviceItem.id">

    <div class="cart-item__image">
      <AtomsImagesIcon icon="laser" class="text-[6rem]" />
    </div>

    <div class="cart-item__description">
      <div class="font-bold text-xl tracking-normal">
        {{ serviceItem.name }}
      </div>
      <div v-html="serviceItem.inputText"></div>
    </div>

    <div class="cart-item__availability">{{ $t('obvykle do {0} prac. dnů', [serviceItem.serviceOption.deliveryPeriod]) }}</div>
    <div class="cart-item__quantity-with-price">
      <div class="cart-item__quantity"></div>
      <div class="cart-item__price">
        <div class="text-xl font-bold">
          <MoleculesGlobalAutocounter :value="serviceItem.serviceOption.price" />
        </div>
      </div>
    </div>

    <button v-if="!props.hideRemoveButton" class="cart-item__remove" @click="removeService(serviceItem)" @mouseover="serviceItem.removeHover = true" @mouseleave="serviceItem.removeHover = false">
      <Transition><span v-show="serviceItem.removeHover">{{ $t('Odebrat položku') }}</span></Transition>
      <AtomsImagesIcon icon="cross" size="xs" class="ml-2" />
    </button>
  </div>
</template>
<script setup>

const props = defineProps({
  cartItemId: Number,
  modelValue: Number,
  wareId: Number | undefined,
  imageId: Number,
  name: String,
  code: String,
  variantName: String,
  url: String,
  availabilityLabel: String,
  piecePriceFormatted: String,
  totalPrice: Number,
  hideRemoveButton: Boolean,
  min: Number | undefined,
  presentationCartItem: Object,
  showLastServiceOnly: Boolean,
  hideServices: Boolean,
  hasLimitedShipping: Boolean | undefined
});

const appConfig = useAppConfig();
const dialog = useDialog();
const locale = useLocale();

const emit = defineEmits(['update:modelValue', 'remove', 'removeService']);

const update = (quantity) => emit('update:modelValue', quantity);

const removeCartItem = () => emit('remove');

const removeService = (service) => emit('removeService', service);

const removeHover = ref(false);

const showEngravingButton = computed(() => {
  let result = true;

  if (!props.presentationCartItem) {
    result = false;
  }
  else {

    // if no services are to be shown (e.g. in added to cart dialog), don´t show the button
    result &= !props.hideServices && !props.showLastServiceOnly;

    // check if engraving service is available
    result &= props.presentationCartItem.availableServices?.some(service => service.id === appConfig.wareServiceIds.engraving)

    // check if there are any items without engraving service
    result &= props.presentationCartItem.orderedServices.filter(service => service.serviceOption.service.id === appConfig.wareServiceIds.engraving).length < props.modelValue
  }

  return result;
});

const orderedServices = computed(() => {

  const result = [];

  if (!props.hideServices && props.presentationCartItem) {
    if (props.showLastServiceOnly) {
      result.push(props.presentationCartItem.orderedServices[props.presentationCartItem.orderedServices.length - 1]);
    }
    else {
      result.push(...props.presentationCartItem.orderedServices);
    }
  }

  return result;
});

const openLimitedShippingDialog = async () => {
  const allCountries = (await useApiFetch(`/api/limitedShippingDetails?idWares=${props.wareId}&currencyId=${locale.getLocale().currencyId}&languageId=${locale.getLocale().languageId}`)).wares[0].shippingOptions.countries;

  dialog.open('limitedShipping', {
    countries: allCountries.checked.map((country) => {
      return {
        id: country.id,
        name: country.name,
        enabled: !allCountries.disabled.some(disabledCountry => disabledCountry.id === country.id)
      }
    })
  });
}

</script>
<style lang="postcss">
.cart-item {
  @apply relative flex items-center justify-between gap-6;

  &__image {
    @apply text-center;
  }

  &__description {
    @apply space-y-2;
  }

  &__availability {
    @apply font-bold uppercase tracking-normal;
  }

  &__quantity {
    @apply min-w-[96px] text-lg;
  }

  &__quantity-with-price {
    @apply flex justify-between items-center gap-6;
  }

  &__price {
    @apply mt-4 font-bold tracking-normal;

    &__piece {
      @apply mt-1 text-xs text-right text-neutral-400;
    }
  }

  &__remove {
    @apply absolute right-4 top-2 hover:text-error-500;
  }

  img {
    @apply max-h-[160px];
  }
}
</style>